import React, { useMemo, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { fuelSolutionsOptions, useDeleteFuelSolution, useValidateFuelSolutionPassword } from "./api";
import type { FuelSolution, FuelStop } from "./api";
import { useAuth0 } from "@auth0/auth0-react";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { AiFillCaretDown, AiFillCaretRight } from "react-icons/ai";
import classNames from "classnames";
import dayjs from "dayjs";
import { FaRegTrashAlt } from "react-icons/fa";
import Swal from "sweetalert2";
import ColumnFilter from "components/column-filter";
import { calculateValueMetrics, type ValueDateRange } from "./value-metrics";

type ValueRangeMode = "this-month" | "last-month" | "last-3-months" | "custom";

const currency = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

export function FuelSolutionsIndex() {
    const { user } = useAuth0();
    const companyCode = user!["https://ifuelsmart.com/company"] as string;
    const { data: fuelSolutions = [], isPending: fuelSolutionsLoading } = useQuery(
        fuelSolutionsOptions(companyCode)
    );
    const { mutateAsync: deleteSolution } = useDeleteFuelSolution();
    const { mutateAsync: validatePassword } = useValidateFuelSolutionPassword();
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [valueRangeMode, setValueRangeMode] = useState<ValueRangeMode>("this-month");
    const [customStart, setCustomStart] = useState(dayjs().subtract(3, "month").format("YYYY-MM"));
    const [customEnd, setCustomEnd] = useState(dayjs().subtract(1, "month").format("YYYY-MM"));

    const valueDateRange = useMemo<ValueDateRange>(() => {
        if (valueRangeMode === "last-3-months") {
            return {
                start: dayjs().startOf("month").subtract(3, "month").toDate(),
                end: dayjs().startOf("month").toDate(),
            };
        }

        if (valueRangeMode === "custom") {
            return {
                start: dayjs(`${customStart}-01`).startOf("month").toDate(),
                end: dayjs(`${customEnd}-01`).add(1, "month").toDate(),
            };
        }

        if (valueRangeMode === "last-month") {
            return {
                start: dayjs().startOf("month").subtract(1, "month").toDate(),
                end: dayjs().startOf("month").toDate(),
            };
        }

        return {
            start: dayjs().startOf("month").toDate(),
            end: dayjs().add(1, "day").startOf("day").toDate(),
        };
    }, [customEnd, customStart, valueRangeMode]);

    const valueMetrics = useMemo(
        () => calculateValueMetrics(fuelSolutions, valueDateRange),
        [fuelSolutions, valueDateRange]
    );

    const valuePeriodLabel = useMemo(() => {
        const start = dayjs(valueDateRange.start);
        const end = dayjs(valueDateRange.end).subtract(1, "day");
        return start.isSame(end, "month")
            ? start.format("MMMM YYYY")
            : `${start.format("MMM YYYY")} to ${end.format("MMM YYYY")}`;
    }, [valueDateRange]);

    const toggleRow = useCallback((fsId: number) => {
        setExpandedRows((prev) => ({
            ...prev,
            [fsId]: !prev[fsId],
        }));
    }, []);

    // This chains two mutations (validate password, then delete), which normally
    // earns a handler hook. It deliberately stays in the component: the flow is a
    // UI-driven wizard (password prompt -> validate -> confirm dialog -> delete),
    // and the coordination - the "don't delete unless validation passed" ordering
    // and early-returns - is inseparable from the Swal dialogs, which a handler
    // hook cannot own. Extracting would only relocate the two try/catch blocks
    // while leaving the real coordination here, so per "layers are earned, not
    // mandatory" it is not worth a hook. The data-access boundary that matters is
    // already held: both mutations and the cache update live in api.ts.
    const handleDeleteFuelSolution = useCallback(
        async (fuelSolutionId: number) => {
            const { value: password, isDismissed } = await Swal.fire({
                title: "Enter your password",
                input: "password",
                inputLabel: "Password",
                inputPlaceholder: "Enter your password",
                showCancelButton: true,
                confirmButtonColor: "#ea580c",
                cancelButtonColor: "#ef4444",
            });

            if (isDismissed) return;

            try {
                Swal.showLoading();
                await validatePassword({
                    password,
                    companyCode,
                });
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: "Invalid password",
                    text: "Please try again",
                    confirmButtonColor: "#ea580c",
                });
                return;
            }

            const swalConfirmation = await Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#ea580c",
                cancelButtonColor: "#ef4444",
                confirmButtonText: "Yes, delete it!",
            });

            if (!swalConfirmation.isConfirmed) return;

            try {
                // Removal from the cached list happens in the hook's onSuccess.
                await deleteSolution({
                    companyCode,
                    fuelSolutionId: fuelSolutionId,
                });
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: "We were unable to delete the fuel solution. Please try again",
                    confirmButtonColor: "#ea580c",
                });
            }
        },
        [companyCode, deleteSolution, validatePassword]
    );

    const columns = useMemo<ColumnDef<FuelSolution>[]>(
        () => [
            {
                id: "expander",
                header: "",
                enableColumnFilter: false,
                cell: ({ row }) => (
                    <button onClick={() => toggleRow(row.original.fsid)} className="focus:outline-none">
                        {expandedRows[row.original.fsid] ? (
                            <AiFillCaretDown className="inline-block text-base" />
                        ) : (
                            <AiFillCaretRight className="inline-block text-base" />
                        )}
                    </button>
                ),
            },
            {
                header: "FSID",
                accessorKey: "fsid",
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                header: "Origin",
                accessorKey: "origin",
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                header: "Destination",
                accessorKey: "destination",
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                header: "Status",
                accessorKey: "status",
                enableColumnFilter: false,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<string>();
                    return (
                        <span
                            className={classNames(
                                "px-2 inline-flex text-xs leading-5 font-semibold rounded-full",
                                value === "QUEUED" ? "bg-yellow-100 text-yellow-800" : "",
                                value === "SENT" ? "bg-green-100 text-green-800" : ""
                            )}
                        >
                            {value}
                        </span>
                    );
                },
            },
            {
                header: "Unit Number",
                accessorKey: "driverId",
                enableSorting: false,
                meta: { filterComponent: ColumnFilter },
            },
            {
                header: "Created At",
                accessorKey: "createdAt",
                enableColumnFilter: false,
                cell: ({ getValue }) => {
                    return <span>{dayjs(getValue<string>()).format("YYYY-MM-DD hh:mm A")}</span>;
                },
            },
            {
                header: "Savings",
                accessorKey: "savings",
                enableColumnFilter: false,
                cell: ({ getValue }) => {
                    return (
                        <span>
                            {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                            }).format(getValue<number>())}
                        </span>
                    );
                },
            },
            {
                id: "actions",
                header: "",
                enableColumnFilter: false,
                enableSorting: false,
                cell: ({ row }) => {
                    return (
                        <div className="flex justify-center space-x-2">
                            <button
                                className="text-gray-400 hover:text-gray-500"
                                onClick={() => handleDeleteFuelSolution(row.original.fsid)}
                            >
                                <FaRegTrashAlt />
                            </button>
                        </div>
                    );
                },
            },
        ],
        [expandedRows, toggleRow, handleDeleteFuelSolution]
    );

    const table = useReactTable({
        data: valueMetrics.includedPlans,
        columns,
        initialState: {
            sorting: [{ id: "createdAt", desc: true }],
            pagination: { pageIndex: 0, pageSize: 100 },
        },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const pageIndex = table.getState().pagination.pageIndex;

    return (
        <>
            <div className="absolute inset-0 flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
                {/* Header */}
                <div className="px-8 py-6 bg-white border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">Fuel Solutions</h1>
                            <p className="mt-1 text-sm text-gray-500">
                                Value delivered in {valuePeriodLabel}
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <select
                                aria-label="Value period"
                                value={valueRangeMode}
                                onChange={(event) => setValueRangeMode(event.target.value as ValueRangeMode)}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-orange-500 focus:outline-none"
                            >
                                <option value="this-month">This month</option>
                                <option value="last-month">Last month</option>
                                <option value="last-3-months">Last 3 months</option>
                                <option value="custom">Custom period</option>
                            </select>
                            {valueRangeMode === "custom" && (
                                <div className="flex items-center gap-2">
                                    <input
                                        aria-label="Start date"
                                        type="month"
                                        value={customStart}
                                        max={customEnd}
                                        onChange={(event) => setCustomStart(event.target.value)}
                                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
                                    />
                                    <span className="text-sm text-gray-400">to</span>
                                    <input
                                        aria-label="End month"
                                        type="month"
                                        value={customEnd}
                                        min={customStart}
                                        max={dayjs().subtract(1, "month").format("YYYY-MM")}
                                        onChange={(event) => setCustomEnd(event.target.value)}
                                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-4">
                        <div className="bg-white shadow-sm rounded-lg px-6 py-3 border border-gray-100">
                            <div className="flex items-center space-x-3">
                                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                                <div>
                                    <div className="text-sm font-medium text-gray-500">Estimated Savings</div>
                                    <div className="text-2xl font-semibold text-[#16a34a]">
                                        {currency.format(valueMetrics.estimatedSavings)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white shadow-sm rounded-lg px-6 py-3 border border-gray-100">
                            <div className="flex items-center space-x-3">
                                <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                                <div>
                                    <div className="text-sm font-medium text-gray-500">Fuel Solutions Sent</div>
                                    <div className="text-2xl font-semibold text-[#2563eb]">
                                        {valueMetrics.plansWithSavings}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="px-8 py-4 bg-white border-b border-gray-200">
                    <div className="flex items-center">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">Page</span>
                            <nav className="flex items-center">
                                <button
                                    onClick={() => table.setPageIndex(0)}
                                    disabled={!table.getCanPreviousPage()}
                                    className={classNames(
                                        "h-8 w-8 flex items-center justify-center rounded-l border border-r-0 border-gray-200",
                                        table.getCanPreviousPage()
                                            ? "text-gray-600 hover:bg-gray-50"
                                            : "text-gray-300 cursor-not-allowed"
                                    )}
                                >
                                    <span>«</span>
                                </button>
                                <button
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage()}
                                    className={classNames(
                                        "h-8 w-8 flex items-center justify-center border border-r-0 border-gray-200",
                                        table.getCanPreviousPage()
                                            ? "text-gray-600 hover:bg-gray-50"
                                            : "text-gray-300 cursor-not-allowed"
                                    )}
                                >
                                    <span>‹</span>
                                </button>
                                <span className="h-8 min-w-[3rem] px-3 inline-flex items-center justify-center bg-white border border-gray-200 text-sm text-gray-700">
                                    {pageIndex + 1} / {table.getPageCount()}
                                </span>
                                <button
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage()}
                                    className={classNames(
                                        "h-8 w-8 flex items-center justify-center border border-l-0 border-gray-200",
                                        table.getCanNextPage()
                                            ? "text-gray-600 hover:bg-gray-50"
                                            : "text-gray-300 cursor-not-allowed"
                                    )}
                                >
                                    <span>›</span>
                                </button>
                                <button
                                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                    disabled={!table.getCanNextPage()}
                                    className={classNames(
                                        "h-8 w-8 flex items-center justify-center rounded-r border border-l-0 border-gray-200",
                                        table.getCanNextPage()
                                            ? "text-gray-600 hover:bg-gray-50"
                                            : "text-gray-300 cursor-not-allowed"
                                    )}
                                >
                                    <span>»</span>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>

                {/* Plans included in the selected value total */}
                <div className="flex-1 min-h-0">
                    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-8 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">Plans Included in This Total</h2>
                        </div>
                        {/* Table */}
                        <div className="flex-1 overflow-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => {
                                                const column = header.column;
                                                const sorted = column.getIsSorted();
                                                const Filter = column.columnDef.meta?.filterComponent;
                                                return (
                                                    <th
                                                        key={header.id}
                                                        scope="col"
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50 border-b border-gray-200"
                                                        onClick={column.getToggleSortingHandler()}
                                                    >
                                                        <div className="flex items-center space-x-1 group">
                                                            <span>
                                                                {flexRender(
                                                                    column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                            </span>
                                                            {column.getCanSort() && (
                                                                <span className="ml-1 flex-none text-gray-400 transition-opacity">
                                                                    {sorted ? (
                                                                        sorted === "desc" ? (
                                                                            <AiFillCaretDown className="h-3 w-3" />
                                                                        ) : (
                                                                            <AiFillCaretDown className="h-3 w-3 rotate-180" />
                                                                        )
                                                                    ) : (
                                                                        <AiFillCaretDown className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {Filter ? <Filter column={column} /> : null}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {fuelSolutionsLoading ? (
                                        <tr>
                                            <td colSpan={table.getHeaderGroups()[0].headers.length}>
                                                <div className="px-6 py-8 text-center">
                                                    <div className="inline-flex items-center space-x-2">
                                                        <svg
                                                            className="animate-spin h-5 w-5 text-gray-500"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            ></circle>
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                            ></path>
                                                        </svg>
                                                        <span className="text-sm text-gray-500">
                                                            Loading solutions...
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        table.getRowModel().rows.map((row) => {
                                            return (
                                                <React.Fragment key={row.original.fsid}>
                                                    <tr className="hover:bg-gray-50 transition-colors cursor-pointer">
                                                        {row.getVisibleCells().map((cell) => (
                                                            <td
                                                                key={cell.id}
                                                                className="px-6 py-4 text-sm whitespace-nowrap"
                                                            >
                                                                {cell.column.id === "savings" ? (
                                                                    <span className="font-medium text-[#16a34a]">
                                                                        {flexRender(
                                                                            cell.column.columnDef.cell,
                                                                            cell.getContext()
                                                                        )}
                                                                    </span>
                                                                ) : cell.column.id === "status" ? (
                                                                    <span
                                                                        className={classNames(
                                                                            "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                                                                            cell.getValue() === "SENT"
                                                                                ? "bg-green-100 text-green-800"
                                                                                : "bg-yellow-100 text-yellow-800"
                                                                        )}
                                                                    >
                                                                        <span className="w-1 h-1 mr-1.5 rounded-full bg-current"></span>
                                                                        {cell.getValue<string>()}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-gray-900">
                                                                        {flexRender(
                                                                            cell.column.columnDef.cell,
                                                                            cell.getContext()
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                    {expandedRows[row.original.fsid] && (
                                                        <tr>
                                                            <td colSpan={columns.length}>
                                                                <div className="px-6 py-4 bg-gray-50">
                                                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                                                        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                                                                            <h3 className="text-sm font-medium text-gray-900">
                                                                                Fuel Stops
                                                                            </h3>
                                                                            <div className="text-sm text-gray-500">
                                                                                Total Stops:{" "}
                                                                                {row.original.stops?.length || 0}
                                                                            </div>
                                                                        </div>
                                                                        <div className="overflow-x-auto">
                                                                            <table className="min-w-full divide-y divide-gray-200">
                                                                                <thead className="bg-gray-50">
                                                                                    <tr>
                                                                                        <th
                                                                                            scope="col"
                                                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                                                                        >
                                                                                            #
                                                                                        </th>
                                                                                        <th
                                                                                            scope="col"
                                                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                                                                        >
                                                                                            Location
                                                                                        </th>
                                                                                        <th
                                                                                            scope="col"
                                                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                                                                        >
                                                                                            Price
                                                                                        </th>
                                                                                        <th
                                                                                            scope="col"
                                                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                                                                        >
                                                                                            Fuel to Purchase
                                                                                        </th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody className="divide-y divide-gray-200">
                                                                                    {row.original.stops?.map(
                                                                                        (stop: FuelStop) => (
                                                                                            <tr
                                                                                                key={stop.stopNumber}
                                                                                                className="hover:bg-gray-50"
                                                                                            >
                                                                                                <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                                                                                                    {stop.stopNumber}
                                                                                                </td>
                                                                                                <td className="px-6 py-3 text-sm text-gray-900 whitespace-nowrap">
                                                                                                    {stop.stopName}
                                                                                                </td>
                                                                                                <td className="px-6 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                                                                    {new Intl.NumberFormat(
                                                                                                        "en-US",
                                                                                                        {
                                                                                                            style: "currency",
                                                                                                            currency:
                                                                                                                "USD",
                                                                                                            minimumFractionDigits: 3,
                                                                                                            maximumFractionDigits: 3,
                                                                                                        }
                                                                                                    ).format(
                                                                                                        stop.fuelPrice
                                                                                                    )}
                                                                                                </td>
                                                                                                <td className="px-6 py-3 text-sm whitespace-nowrap">
                                                                                                    {stop.fuelToPurchase ===
                                                                                                    -99 ? (
                                                                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                                                            <span className="w-1 h-1 mr-1.5 rounded-full bg-current"></span>
                                                                                                            Top off
                                                                                                        </span>
                                                                                                    ) : (
                                                                                                        <span className="font-medium text-gray-900">
                                                                                                            {stop.fuelToPurchase.toLocaleString()}{" "}
                                                                                                            gal
                                                                                                        </span>
                                                                                                    )}
                                                                                                </td>
                                                                                            </tr>
                                                                                        )
                                                                                    )}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
